import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlacesAutocomplete } from "@/components/ui/places-autocomplete";
import { Textarea } from "@/components/ui/textarea";
import { useAgencyOperations } from "@/features/agencies/hooks/useAgencies";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/dashboard/admin/agencies/create")({
  head: () => ({
    meta: [
      {
        title: "Create Agency - GetHandle Admin",
      },
    ],
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { createAgency, loading: creating, error: createError } = useAgencyOperations();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    emails: [""],
    phoneNumbers: [""],
    notes: "",
  });

  const handleCreateAgency = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const agencyData = {
        ...formData,
        email: formData.email.trim() || undefined,
        emails: formData.emails.filter((email) => email.trim() !== ""),
        phoneNumbers: formData.phoneNumbers.filter((phone) => phone.trim() !== ""),
        address: Object.values(formData.address).some((val) => val.trim() !== "")
          ? formData.address
          : undefined,
      };

      const result = await createAgency(agencyData);

      // Navigate to the new agency page
      if (result?.id) {
        navigate({
          to: "/dashboard/admin/agencies/$agencyId",
          params: { agencyId: result.id },
        });
      } else {
        // Fallback to agencies list
        navigate({ to: "/dashboard/admin/agencies" });
      }
    } catch (error) {
      console.error("Error creating agency:", error);
    }
  };

  const addEmailField = () => {
    setFormData((prev) => ({
      ...prev,
      emails: [...prev.emails, ""],
    }));
  };

  const removeEmailField = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      emails: prev.emails.filter((_, i) => i !== index),
    }));
  };

  const updateEmail = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      emails: prev.emails.map((email, i) => (i === index ? value : email)),
    }));
  };

  const addPhoneField = () => {
    setFormData((prev) => ({
      ...prev,
      phoneNumbers: [...prev.phoneNumbers, ""],
    }));
  };

  const removePhoneField = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.filter((_, i) => i !== index),
    }));
  };

  const updatePhone = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      phoneNumbers: prev.phoneNumbers.map((phone, i) => (i === index ? value : phone)),
    }));
  };

  return (
    <div className="container mx-auto max-w-3xl space-y-6 p-1 sm:p-6">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Create New Agency</h2>
        <p className="text-muted-foreground">
          Add a new agency with detailed information including contact details and address.
        </p>
      </div>

      <form onSubmit={handleCreateAgency} className="space-y-6">
        <div className="rounded-lg border p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Agency name"
            />
          </div>

          {/* Primary Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Primary Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="agency@example.com"
            />
            <p className="text-muted-foreground text-xs">
              Used for login credentials if agency is invited to access the system
            </p>
          </div>

          {/* Emails */}
          <div className="space-y-2">
            <Label>Emails</Label>
            <div className="space-y-2">
              {formData.emails.map((email, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => updateEmail(index, e.target.value)}
                    placeholder="agency@email.com"
                  />
                  {formData.emails.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeEmailField(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addEmailField}>
                Add Email
              </Button>
            </div>
          </div>

          {/* Phone Numbers */}
          <div className="space-y-2">
            <Label>Phone Numbers</Label>
            <div className="space-y-2">
              {formData.phoneNumbers.map((phone, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => updatePhone(index, e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                  {formData.phoneNumbers.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removePhoneField(index)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addPhoneField}>
                Add Phone
              </Button>
            </div>
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label>Address</Label>
            <div className="space-y-2">
              <PlacesAutocomplete
                value={formData.address.street}
                onChange={(value) =>
                  setFormData({
                    ...formData,
                    address: {
                      ...formData.address,
                      street: value,
                    },
                  })
                }
                placeholder="Search for street address..."
                types="geocode"
                language="en"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="City"
                  value={formData.address.city}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: {
                        ...formData.address,
                        city: e.target.value,
                      },
                    })
                  }
                />
                <Input
                  placeholder="State"
                  value={formData.address.state}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: {
                        ...formData.address,
                        state: e.target.value,
                      },
                    })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Zip Code"
                  value={formData.address.zipCode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: {
                        ...formData.address,
                        zipCode: e.target.value,
                      },
                    })
                  }
                />
                <Input
                  placeholder="Country"
                  value={formData.address.country}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      address: {
                        ...formData.address,
                        country: e.target.value,
                      },
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes about this agency..."
              rows={3}
            />
          </div>
        </div>

        {createError && (
          <Alert variant="destructive">
            <AlertDescription>{createError}</AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ to: "/dashboard/admin/agencies" })}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={creating}>
            {creating ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Agency"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
